import React, { useEffect, useRef, useState } from 'react';

const DEBOUNCE_MS = 250;

export default function WarehouseSearchBox({
    storeKey, shopifyLocationId, apiFetch,
    onProductPicked, onError, onScanFlash, onScanClick,
}) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const inputRef = useRef(null);
    const debounceTimer = useRef(null);
    const reqIdRef = useRef(0);

    useEffect(() => { inputRef.current?.focus(); }, [shopifyLocationId]);

    useEffect(() => () => clearTimeout(debounceTimer.current), []);

    const runSearch = async (term, { exact } = {}) => {
        if (!term.trim()) { setResults([]); setOpen(false); return; }
        const reqId = ++reqIdRef.current;
        setBusy(true);
        try {
            const qs = `q=${encodeURIComponent(term.trim())}&shopify_location_id=${encodeURIComponent(shopifyLocationId)}${exact ? '&exact=1' : ''}`;
            const r = await apiFetch(`/api/portal/warehouse/search?${qs}`);
            if (reqId !== reqIdRef.current) return;
            if (!r.ok) { onError?.(`Search failed (${r.status})`); return; }
            const data = await r.json();
            const items = data.results || [];
            setResults(items);
            setOpen(items.length > 0);
            if (exact && items.length === 1 && items[0].matched_variant_gid) {
                onProductPicked?.({ product_gid: items[0].product_gid, matched_variant_gid: items[0].matched_variant_gid });
                onScanFlash?.();
                setQuery('');
                setResults([]);
                setOpen(false);
            }
        } catch (e) {
            onError?.('Search failed (network)');
        } finally {
            if (reqId === reqIdRef.current) setBusy(false);
        }
    };

    const onChange = (e) => {
        const v = e.target.value;
        setQuery(v);
        clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => runSearch(v), DEBOUNCE_MS);
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            clearTimeout(debounceTimer.current);
            runSearch(query, { exact: true });
        } else if (e.key === 'Escape') {
            setOpen(false);
        }
    };

    return (
        <div style={{ position: 'relative' }}>
            <div style={pillStyle}>
                {searchIcon}
                <input
                    ref={inputRef}
                    value={query}
                    onChange={onChange}
                    onKeyDown={onKeyDown}
                    onFocus={() => results.length && setOpen(true)}
                    placeholder="Scan barcode or type SKU"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck="false"
                    style={inputStyle}
                />
                <button
                    type="button"
                    onClick={() => onScanClick?.()}
                    style={scanBtnStyle}
                    aria-label="Open camera scanner"
                    title="Scan barcode with camera"
                >
                    {cameraIcon}
                </button>
            </div>
            {open && results.length > 0 && (
                <div style={dropdownStyle}>
                    {results.map(r => (
                        <button
                            key={r.product_gid}
                            type="button"
                            onClick={() => {
                                onProductPicked?.({ product_gid: r.product_gid, matched_variant_gid: r.matched_variant_gid });
                                setQuery(''); setResults([]); setOpen(false);
                            }}
                            style={rowBtnStyle}
                        >
                            <div style={thumbStyle}>
                                {r.image
                                    ? <img src={r.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} />
                                    : glyphForType(r.product_type)
                                }
                            </div>
                            <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                                <div style={titleStyle}>{r.title}</div>
                                <div style={metaStyle}>{r.vendor}{r.variant_count != null ? ` · ${r.variant_count} variants` : ''}</div>
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

const pillStyle = {
    display: 'flex', alignItems: 'center', gap: 10,
    height: 48, background: '#fff', borderRadius: 13,
    padding: '0 7px 0 14px',
    border: '1px solid #d9dde3',
    boxShadow: '0 1px 2px rgba(14, 24, 48, 0.04)',
};
const inputStyle = {
    flex: 1, minWidth: 0, border: 'none', background: 'transparent',
    fontSize: 16, color: '#202223', outline: 'none', fontFamily: 'inherit',
};
const scanBtnStyle = {
    width: 38, height: 38, flex: 'none', border: 'none', borderRadius: 9,
    background: '#1a4fa0', color: '#fff', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const dropdownStyle = {
    position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
    background: '#fff', borderRadius: 13,
    boxShadow: '0 18px 44px rgba(14,24,48,0.28)',
    overflow: 'hidden', zIndex: 30, maxHeight: 360, overflowY: 'auto',
};
const rowBtnStyle = {
    width: '100%', display: 'flex', alignItems: 'center', gap: 11,
    padding: '11px 14px', border: 'none', borderBottom: '1px solid #f0f1f3',
    background: '#fff', cursor: 'pointer',
};
const thumbStyle = {
    width: 34, height: 34, flex: 'none', borderRadius: 8,
    background: '#f3f4f6', border: '1px solid #e5e7eb',
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af',
    overflow: 'hidden',
};
const titleStyle = {
    fontSize: 13.5, fontWeight: 600, color: '#202223',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
};
const metaStyle = { fontSize: 11.5, color: '#6d7175' };

const searchIcon = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></svg>
);
const cameraIcon = (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
        <circle cx="12" cy="13" r="4"/>
    </svg>
);
function glyphForType(t) {
    return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7l4-3h8l4 3v3a2 2 0 0 1-2 2v8H6v-8a2 2 0 0 1-2-2V7z"/></svg>;
}
